import { defaults, createStore, createHook } from 'react-sweet-state';

import { getTodaysGameId, getGameId, getUnlimitedGameId } from 'common/logic';
import api from 'common/api';
import dayjs from 'dayjs';
import { sleep } from 'common/utils';
import { track } from 'common/analytics';
import { getToday } from 'common/logic';
import i18n from 'i18n';

const validLanguages = ['en', 'es', 'pt', 'no'];

defaults.devtools = process.env.NODE_ENV === 'development';

const defaultGameData = (gameSpec) => ({
  status: 'selecting',
  typed: '',
  cursorIndex: 0,
  attempts: [],
  version: gameSpec?.version,
});

const getInternalFormatDate = (date) => {
  if (!date) {
    return getToday().format('YYYY-MM-DD');
  }
  return dayjs(date).format('YYYY-MM-DD');
};

const toast =
  (message) =>
  async ({ setState, getState }) => {
    setState({
      ...getState(),
      ui: {
        ...getState().ui,
        toast: message,
      },
    });
    await sleep(2000);
    setState({
      ...getState(),
      ui: {
        ...getState().ui,
        toast: null,
      },
    });
  };

const handleSubmission =
  (dictionary) =>
  async ({ setState, getState, dispatch }) => {
    const { typed } = getState().data;
    if (typed.length === 0) {
      return;
    }
    setState({
      ...getState(),
      data: {
        ...getState().data,
        status: 'checking',
      },
    });
    const typedWithAccents = dictionary.accents[typed] || typed;
    if (!dictionary.words.has(typedWithAccents)) {
      dispatch(toast(i18n.t('gameScreen.invalidWord')));
      setState({
        ...getState(),
        data: {
          ...getState().data,
          status: 'selecting',
        },
      });
      return;
    }
    const { attempts } = getState().data;
    const { id: gameId, language, mode } = getState();
    if (attempts.length === 0) {
      track.gameStart(gameId, language, mode);
    }
    track.gameAttempt(gameId, language, mode);
    const { answer } = getState().spec;
    if (typedWithAccents === answer) {
      track.gameWin(gameId, language, mode);
      setState({
        ...getState(),
        data: {
          ...getState().data,
          status: 'animating',
          typed: '',
          cursorIndex: 0,
          attempts: [...getState().data.attempts, typedWithAccents],
        },
      });
      await sleep(1500);
      setState({
        ...getState(),
        data: {
          ...getState().data,
          status: 'won',
        },
      });
      return;
    }
    setState({
      ...getState(),
      data: {
        ...getState().data,
        status: 'selecting',
        typed: '',
        cursorIndex: 0,
        attempts: [...getState().data.attempts, typedWithAccents],
      },
    });
  };

const gameStore = createStore({
  initialState: {
    status: 'unprepared',
    language: null,
    id: null,
    title: null,
    ui: { toast: null },
    spec: null,
    data: null,
    mode: null,
  },
  actions: {
    prepare:
      (language, gameId, gameTitle, gameSpec, gameData, mode = null) =>
      ({ setState }) => {
        setState({
          status: 'prepared',
          language,
          id: gameId,
          title: gameTitle,
          spec: gameSpec,
          data: gameData || defaultGameData(gameSpec),
          mode,
        });
      },
    positionCursor:
      (index) =>
      ({ setState, getState }) => {
        if (getState().data.status !== 'selecting') {
          return;
        }
        const { typed } = getState().data;
        if (index < 0 || index > typed.length) {
          return;
        }
        const newData = { ...getState().data };
        newData.cursorIndex = index;
        setState({
          ...getState(),
          data: newData,
        });
      },
    input:
      (key, dictionary) =>
      ({ setState, getState, dispatch }) => {
        if (getState().data.status !== 'selecting') {
          return;
        }
        if (key === 'Enter') {
          dispatch(handleSubmission(dictionary));
          return;
        }
        const newData = { ...getState().data };
        if (key === 'ArrowLeft') {
          newData.cursorIndex = Math.max(0, newData.cursorIndex - 1);
        }
        if (key === 'ArrowRight') {
          newData.cursorIndex = Math.min(
            newData.typed.length,
            newData.cursorIndex + 1,
          );
        }
        if (key === 'Backspace') {
          const { typed, cursorIndex } = newData;
          if (cursorIndex === 0) {
            return;
          }
          const newTyped =
            typed.substring(0, cursorIndex - 1) + typed.substring(cursorIndex);
          newData.typed = newTyped;
          newData.cursorIndex = cursorIndex - 1;
        }
        if (key === 'Delete') {
          const { typed, cursorIndex } = newData;
          if (cursorIndex === typed.length) {
            return;
          }
          const newTyped =
            typed.substring(0, cursorIndex) + typed.substring(cursorIndex + 1);
          newData.typed = newTyped;
        }
        const allowedChars =
          'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZæøåÆØÅ';
        if (allowedChars.includes(key)) {
          const { typed, cursorIndex } = newData;
          if (typed.length >= 10) {
            return;
          }
          const newTyped =
            typed.substring(0, cursorIndex) +
            key +
            typed.substring(cursorIndex);
          newData.typed = newTyped.toLowerCase();
          newData.cursorIndex = cursorIndex + 1;
        }
        setState({
          ...getState(),
          data: newData,
        });
      },
  },
  name: 'game',
});

const useGame = createHook(gameStore);

const dailyStore = createStore({
  initialState: {
    status: 'unloaded',
    language: null,
    date: null,
    gameId: null,
    gameSpec: null,
  },
  actions: {
    load:
      (language) =>
      async ({ setState, getState }) => {
        const todaysGameId = getTodaysGameId(language);
        if (
          getState().gameId === todaysGameId &&
          getState().language === language
        ) {
          return;
        }
        if (!language || !validLanguages.includes(language)) {
          return;
        }
        const date = getInternalFormatDate();
        const todaysGameSpec = await api.loadGameSpec(todaysGameId, language);
        setState({
          status: 'loaded',
          language,
          date,
          gameId: todaysGameId,
          gameSpec: todaysGameSpec,
        });
      },
  },
  name: 'daily',
});

const useDaily = createHook(dailyStore);

const dictionaryStore = createStore({
  initialState: {
    language: null,
    words: null,
    accents: null,
  },
  actions: {
    load:
      (language) =>
      async ({ setState, getState }) => {
        if (getState().language === language) {
          return;
        }
        const { default: allWords } = await import(
          `../words/all-${language}.json`
        );
        const words = new Set(allWords);
        const { default: accents } = await import(
          `../words/accents-${language}.json`
        );
        setState({
          language,
          words,
          accents,
        });
      },
  },
  name: 'dictionary',
});

const useDictionary = createHook(dictionaryStore);

const previousStore = createStore({
  initialState: {
    status: 'unloaded',
    language: null,
    date: null,
    gameId: null,
    gameSpec: null,
  },
  actions: {
    load:
      (date, language) =>
      async ({ setState, getState }) => {
        const today = getToday().endOf('day');
        if (dayjs(date).isAfter(today)) {
          return;
        }
        const gameId = getGameId(date, language);
        const dateString = getInternalFormatDate(date);
        if (getState().gameId === gameId && getState().language === language) {
          return;
        }
        if (!language || !validLanguages.includes(language)) {
          return;
        }
        setState({
          status: 'loading',
          language: null,
          date: null,
          gameId: null,
          gameSpec: null,
        });
        const gameSpec = await api.loadGameSpec(gameId, language);
        setState({
          status: 'loaded',
          language,
          date: dateString,
          gameId,
          gameSpec,
        });
      },
  },
  name: 'previous',
});

const usePrevious = createHook(previousStore);

const unlimitedStore = createStore({
  initialState: {
    status: 'unloaded',
    language: null,
    gameId: null,
    gameSpec: null,
  },
  actions: {
    load:
      (language) =>
      async ({ setState }) => {
        setState({
          status: 'loading',
        });
        const gameId = getUnlimitedGameId(language);
        const gameSpec = await api.loadGameSpec(gameId, language);
        setState({
          status: 'loaded',
          language,
          gameId,
          gameSpec,
        });
      },
  },
  name: 'unlimited',
});

const useUnlimited = createHook(unlimitedStore);

export { useGame, useDaily, useDictionary, usePrevious, useUnlimited };
